import { ListAltTwoTone } from '@mui/icons-material';
import { Stack } from '@mui/material';
import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { formatDateString, FormGrid, IParams, Page, PermissionKeys, useHasPermission, Widget } from '../../../shared';
import {
    ProgressCategories,
    ProgressProducts,
    ShipmentDetailActions,
    ShipmentDetailInfo,
    ShipmentDetailStockMovements,
} from '../../components';
import { ShipmentDetailAttachments } from '../../components/shipment-detail/shipment-detail-attachments.component';
import { useDeleteShipment, useShipment, useShipmentProgress } from '../../hooks';

export const ShipmentDetailPage: FC = () => {
    const { t } = useTranslation();
    const { id } = useParams<keyof IParams>() as IParams;
    const navigate = useNavigate();

    const { state: redirectToDashboard } = useLocation();
    const { hasPermission } = useHasPermission();
    const hasWritePermission = hasPermission(PermissionKeys.SHIPMENTS_WRITE);

    const { data: shipment, isPending: shipmentIsPending } = useShipment(id);
    const { data: progresses } = useShipmentProgress(id, 10000);

    const { mutateAsync: deleteShipment, isPending: isPendingDelete } = useDeleteShipment();

    const onlyProducts = useMemo(() => {
        return progresses?.every(({ product }) => product && product?.requiresReservation) || false;
    }, [progresses]);

    return (
        <Page
            title={t(`shipmentDetails`, {
                organisation: shipment && shipment.receiver?.name,
                date: shipment && formatDateString(shipment.shipmentDate),
                interpolation: { escapeValue: false },
            })}
            onBack={() => navigate(redirectToDashboard ? redirectToDashboard : '/planning/shipments')}
            loading={shipmentIsPending || isPendingDelete}
            actions={
                hasWritePermission && (
                    <ShipmentDetailActions id={id} shipment={shipment} deleteShipment={deleteShipment} />
                )
            }
        >
            {shipment && (
                <Stack spacing={2}>
                    <FormGrid size={{ xs: 6 }} itemProps={{ '&.MuiGrid-root:first-child': { pl: 0 } }}>
                        <ShipmentDetailInfo shipment={shipment} />
                        <ShipmentDetailAttachments shipment={shipment} />
                    </FormGrid>

                    {!onlyProducts && progresses && (
                        <Widget title={t('progressesCategories')} icon={<ListAltTwoTone color="primary" />}>
                            <ProgressCategories
                                progresses={progresses}
                                showLabels
                                chartsPerRow={8}
                                blacklistedCategories={[]}
                            />
                        </Widget>
                    )}

                    {progresses && <ProgressProducts progresses={progresses} chartsPerRow={8} />}

                    <ShipmentDetailStockMovements shipment={shipment} />
                </Stack>
            )}
        </Page>
    );
};
